import PageSEO from '@/components/PageSEO';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { CheckCircle, Phone, Lightbulb, Users, TrendingUp } from 'lucide-react';
import ProudlyServingSection from '@/components/ProudlyServingSection';

export const OfficePartitionsPage = () => {
  const serviceSchema = {
    "@context": "https://schema.org",
    "@type": "Service",
    "serviceType": "Glass Office Partition Installation",
    "name": "Glass Office Partitions & Dividers",
    "description": "Modern glass office partitions and dividers including floor-to-ceiling walls, frameless options, and sound-dampening glass for commercial spaces.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Comal Glass Company",
      "telephone": "+1-830-359-9925"
    },
    "areaServed": { "@type": "State", "name": "Texas" }
  };

  const features = [
    'Floor-to-ceiling glass walls',
    'Frameless and framed options',
    'Sound-dampening glass available',
    'Frosted and clear glass choices',
    'Custom door integration',
    'Cable management solutions',
    'Quick installation process',
    'Minimal workplace disruption',
  ];

  const benefits = [
    {
      icon: Lightbulb,
      title: 'Maximize Natural Light',
      description: 'Glass partitions allow light to flow throughout the office, reducing energy costs and improving mood.',
    },
    {
      icon: Users,
      title: 'Encourage Collaboration',
      description: 'Visual connectivity promotes teamwork while maintaining separate work zones.',
    },
    {
      icon: TrendingUp,
      title: 'Modern Professional Image',
      description: 'Elevate your workspace with sleek, contemporary glass dividers that impress clients.',
    },
  ];

  return (
    <div className="min-h-screen">
      <PageSEO title="Glass Office Partitions" description="Modern glass office partitions and dividers. Floor-to-ceiling, frameless, sound-dampening, and frosted options. Minimal disruption installation for Hill Country businesses." path="/office-partitions" />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceSchema)}</script>
      </Helmet>
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img
            src="/images/services/U-channel-Office-Enclosure.jpeg"
            alt="Modern office glass partitions"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/60 to-black/40" />
        </div>

        <div className="container-custom relative z-10 text-white">
          <div className="max-w-3xl">
            <Badge className="mb-6 bg-primary/20 text-primary border-primary/30 backdrop-blur-sm">
              Modern Workspace Solutions
            </Badge>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              Glass <span className="text-gradient-cyan">Office Partitions</span>
            </h1>
            <p className="text-lg sm:text-xl text-gray-200 mb-8 leading-relaxed">
              Create open, collaborative workspaces with modern glass office dividers. Maximize light, maintain privacy, and inspire productivity.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10" asChild>
                <a href="tel:8303599925">
                  <Phone className="mr-2 h-5 w-5" />
                  Call Now
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">The Advantages</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Transform Your <span className="text-gradient-cyan">Workspace</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Glass office partitions offer a perfect blend of openness and functionality.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => {
              const Icon = benefit.icon;
              return (
                <Card key={index} className="border-none shadow-md hover:shadow-lg transition-shadow">
                  <CardContent className="pt-6">
                    <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center mb-4">
                      <Icon className="h-8 w-8 text-primary" />
                    </div>
                    <h3 className="text-xl font-bold mb-2">{benefit.title}</h3>
                    <p className="text-muted-foreground">{benefit.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Features & Options</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Customizable <span className="text-gradient-cyan">Solutions</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="border-none shadow-md">
                <CardContent className="pt-6">
                  <div className="flex items-start space-x-3">
                    <CheckCircle className="h-5 w-5 text-primary flex-shrink-0 mt-1" />
                    <p className="text-sm">{feature}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Applications Section */}
      <section className="py-20 bg-secondary text-secondary-foreground">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4 bg-primary/20 text-primary border-primary/30">Applications</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Perfect for Every <span className="text-gradient-cyan">Space</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { title: 'Conference Rooms', description: 'Professional meeting spaces with acoustic privacy' },
              { title: 'Private Offices', description: 'Executive offices that maintain visual connectivity' },
              { title: 'Collaborative Zones', description: 'Flexible spaces for team projects and brainstorming' },
              { title: 'Reception Areas', description: 'Elegant entrance spaces that make lasting impressions' },
            ].map((app, index) => (
              <Card key={index} className="border-none bg-card">
                <CardContent className="pt-6 text-center">
                  <h3 className="text-xl font-semibold mb-2">{app.title}</h3>
                  <p className="text-muted-foreground text-sm">{app.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Glass Options Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="text-center mb-16">
            <Badge className="mb-4">Glass Types</Badge>
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Choose Your <span className="text-gradient-cyan">Privacy Level</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-2">Clear Glass</h3>
                <p className="text-muted-foreground mb-4">
                  Maximum transparency for open, collaborative environments.
                </p>
                <ul className="text-sm space-y-2">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>Full visibility</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>Maximum light transmission</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-2">Frosted Glass</h3>
                <p className="text-muted-foreground mb-4">
                  Balanced privacy with natural light flow.
                </p>
                <ul className="text-sm space-y-2">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>Visual privacy</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>Diffused light</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            <Card className="border-none shadow-md">
              <CardContent className="pt-6">
                <h3 className="text-xl font-bold mb-2">Acoustic Glass</h3>
                <p className="text-muted-foreground mb-4">
                  Sound-dampening for confidential spaces.
                </p>
                <ul className="text-sm space-y-2">
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>Noise reduction</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-0.5" />
                    <span>Meeting privacy</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Gallery Preview */}
      <section className="py-20 bg-gradient-subtle">
        <div className="container-custom">
          <div className="text-center mb-12">
            <h2 className="text-3xl sm:text-4xl font-bold mb-4">
              Recent <span className="text-gradient-cyan">Office Projects</span>
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {[
              '/images/services/glass-restaurant-partition.jpg',
              '/images/portfolio/Hydraulic-Patches.jpeg',
              '/images/services/Office-Partition.jpeg',
            ].map((image, index) => (
              <div key={index} className="aspect-square overflow-hidden rounded-lg shadow-lg">
                <img src={image} alt={`Office project ${index + 1}`} className="w-full h-full object-cover" />
              </div>
            ))}
          </div>

          <div className="text-center">
            <Button variant="outline" size="lg" asChild>
              <Link to="/portfolio">View All Projects</Link>
            </Button>
          </div>
        </div>
      </section>

      {/* Proudly Serving Section */}
      <ProudlyServingSection serviceSlug="office-partitions" />

      {/* CTA Section */}
      <section className="py-20">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl sm:text-4xl font-bold mb-6">
              Modernize Your <span className="text-gradient-cyan">Office Space</span>
            </h2>
            <p className="text-lg text-muted-foreground mb-8">
              Let's create a workspace that inspires productivity and impresses clients.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button size="lg" className="bg-primary hover:bg-primary/90 glow-hover" asChild>
                <Link to="/contact">Get Free Quote</Link>
              </Button>
              <Button size="lg" variant="outline" asChild>
                <a href="tel:8303599925">
                  <Phone className="mr-2 h-5 w-5" />
                  (830) 359-9925
                </a>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OfficePartitionsPage;
